import React, { useState, useEffect } from 'react';
import apiClient from '../../apiClient';
import { FaTimes, FaCheck, FaRocket } from 'react-icons/fa';
import './StartTrialModal.css';

function StartTrialModal({ isOpen, onClose, onTrialStarted }) {
  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      fetchPlans();
    }
  }, [isOpen]);

  const fetchPlans = async () => {
    try {
      const response = await apiClient.get('/paywall/subscriptions/plans');
      // Filter out free plan and only show plans with trials
      const trialPlans = response.data.filter(plan => 
        plan.price_monthly > 0 && plan.trial_days > 0
      );
      setPlans(trialPlans);
      if (trialPlans.length > 0) {
        setSelectedPlan(trialPlans[0].id);
      }
    } catch (error) {
      console.error('Error fetching plans:', error);
      setError('Failed to load subscription plans');
    }
  };

  const handleStartTrial = async () => {
    if (!selectedPlan) return;

    setLoading(true);
    setError('');

    try {
      await apiClient.post('/trial/start', {
        plan_id: selectedPlan
      });
      
      onTrialStarted();
      onClose();
    } catch (error) {
      console.error('Error starting trial:', error);
      setError(error.response?.data?.error || 'Failed to start trial');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content start-trial-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Start Your Free Trial</h2>
          <button className="close-button" onClick={onClose}>
            <FaTimes />
          </button>
        </div>

        <div className="modal-body">
          <div className="trial-intro">
            <FaRocket className="trial-icon" />
            <p>Experience all premium features with no commitment. No credit card required!</p>
          </div>

          {error && (
            <div className="error-message">{error}</div>
          )}

          <div className="plan-selection">
            <h3>Select a plan to trial:</h3>
            {plans.map(plan => (
              <div 
                key={plan.id}
                className={`plan-option ${selectedPlan === plan.id ? 'selected' : ''}`}
                onClick={() => setSelectedPlan(plan.id)}
              >
                <div className="plan-radio">
                  {selectedPlan === plan.id && <FaCheck />}
                </div>
                <div className="plan-details">
                  <div className="plan-header">
                    <span className="plan-name">{plan.name}</span>
                    <span className="plan-price">${plan.price_monthly}/month</span>
                  </div>
                  <div className="plan-trial">{plan.trial_days}-day free trial</div>
                  <ul className="plan-features">
                    {plan.features && Object.entries(plan.features).map(([key, value]) => (
                      <li key={key}>
                        {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}: {
                          value === null ? 'Unlimited' : value
                        }
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>

          <div className="trial-benefits">
            <h3>What's included in your trial:</h3>
            <ul>
              <li><FaCheck /> Full access to all features</li>
              <li><FaCheck /> No credit card required</li>
              <li><FaCheck /> Cancel anytime</li>
              <li><FaCheck /> Email reminders before trial ends</li>
            </ul>
          </div>
        </div>

        <div className="modal-footer">
          <button 
            className="cancel-button" 
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </button>
          <button 
            className="start-trial-button" 
            onClick={handleStartTrial}
            disabled={loading || !selectedPlan}
          >
            {loading ? 'Starting...' : 'Start Free Trial'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default StartTrialModal;